import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Snackbar,
  Alert,
} from '@mui/material';
import { differenceInBusinessDays, parseISO } from 'date-fns';
import { db } from '../../firebase';
import { doc, setDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { ApplyLeaveModal, ViewLeaveHistoryModal } from './LeaveModals';
import AnnualLeave from './annualLeave';

const EAttendance = ({ user }) => {
  const [leaveRequest, setLeaveRequest] = useState({
    startDate: '',
    endDate: '',
    status: null,
    totalHours: 0,
  });
  const [leaves, setLeaves] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success'); // 'success', 'error', etc.

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const calculateWorkingHours = (startDate, endDate) => {
    if (!startDate || !endDate) return 0;
    const businessDays =
      differenceInBusinessDays(parseISO(endDate), parseISO(startDate)) + 1;
    return businessDays * 9; // 9 hours/day
  };

  const saveLeaveToDatabase = async (leaveData) => {
    try {
      const leaveId = `${user.id}-${leaveData.startDate}-${leaveData.endDate}`;
      await setDoc(doc(db, 'employeeLeave', leaveId), {
        ...leaveData,
        timestamp: new Date().toISOString(),
      });
      setSnackbarMessage('Leave request sent successfully.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error saving leave to database:', error);
      setSnackbarMessage('Failed to save leave request.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const fetchLeaves = useCallback(async () => {
    try {
      const q = query(collection(db, 'employeeLeave'), where('employeeId', '==', user.id));
      const querySnapshot = await getDocs(q);
      const fetchedLeaves = querySnapshot.docs.map((doc) => doc.data());
      setLeaves(fetchedLeaves);
    } catch (error) {
      console.error('Error fetching leaves:', error);
    }
  }, [user.id]);

  const handleApplyLeave = () => setOpenModal(true);
  const handleConfirmLeave = async () => {
    if (leaveRequest.startDate && leaveRequest.endDate) {
      const totalHours = calculateWorkingHours(leaveRequest.startDate, leaveRequest.endDate);
      const newLeaveRequest = {
        startDate: leaveRequest.startDate,
        endDate: leaveRequest.endDate,
        description: leaveRequest.description || '',
        totalHours,
        status: 'Pending',
        employeeId: user.id,
      };
      setLeaveRequest((prev) => ({
        ...prev,
        ...newLeaveRequest,
      }));
      await saveLeaveToDatabase(newLeaveRequest);
      setOpenModal(false);
      await fetchLeaves();
    } else {
      setSnackbarMessage('Please select both start and end dates to apply for the leave.');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
    }
  };

  const handleViewLeaves = async () => {
    await fetchLeaves();
    setOpenViewModal(true);
  };

  const handleCloseModal = () => setOpenModal(false);
  const handleCloseViewModal = () => setOpenViewModal(false);

  const getStatusStyle = (status) => {
    if (status === 'Approved') {
      return {
        backgroundColor: '#39B54A',
        color: 'white',
        padding: '5px 10px',
        borderRadius: '4px',
        textAlign: 'center',
      };
    } else if (status === 'Pending') {
      return {
        backgroundColor: '#FFA500',
        color: 'white',
        padding: '5px 10px',
        borderRadius: '4px',
        textAlign: 'center',
      };
    } else if (status === 'Disapproved') {
      return {
        backgroundColor: '#d32f2f',
        color: 'white',
        padding: '5px 10px',
        borderRadius: '4px',
        textAlign: 'center',
      };
    }
  };

  useEffect(() => {
    fetchLeaves();
  }, [fetchLeaves]);

  return (
    <Box sx={{ flexGrow: 1, padding: 3, paddingTop: 0, marginTop: 0 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ color: '#39B54A' }}>
          Leave Management
        </Typography>
      </Box>
      {/* Leave Status Component */}
      <AnnualLeave user={user} />
      {/* Buttons Section */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          marginTop: 3,
          justifyContent: 'space-between',
          backgroundColor: 'white',
          height: 60,
          borderRadius: 1,
        }}
      >
        <Button
          variant="outlined"
          onClick={handleApplyLeave}
          sx={{
            mt: 1,
            mb: 1,
            ml: 2,
            color: '#39B54A',
            borderColor: '#39B54A',
            '&:hover': { borderColor: '#2c8f39', color: '#2c8f39' },
          }}
        >
          Apply Leave
        </Button>
        <Button
          variant="outlined"
          onClick={handleViewLeaves}
          sx={{
            mt: 1,
            mb: 1,
            mr: 2,
            color: '#39B54A',
            borderColor: '#39B54A',
            '&:hover': { borderColor: '#2c8f39', color: '#2c8f39' },
          }}
        >
          View Leave History
        </Button>
      </Box>

      {/* Modals */}
      <ApplyLeaveModal
        openModal={openModal}
        handleCloseModal={handleCloseModal}
        leaveRequest={leaveRequest}
        setLeaveRequest={setLeaveRequest}
        handleConfirmLeave={handleConfirmLeave}
      />
      <ViewLeaveHistoryModal
        openViewModal={openViewModal}
        handleCloseViewModal={handleCloseViewModal}
        leaves={leaves}
        getStatusStyle={getStatusStyle}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} 
         sx={{ backgroundColor: 'white', color: snackbarSeverity === 'success' ? 'green' : 'red' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EAttendance;
